import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import receiptVoucherService, {
  ReceiptVoucher,
  CreateReceiptVoucherPayload,
  ReceiptVoucherFilters,
  ArSettlementContext,
} from '@/services/receiptVoucher.service';

export function useReceiptVouchers(filters?: ReceiptVoucherFilters) {
  return useQuery({
    queryKey: ['receipt-vouchers', filters],
    queryFn: () => receiptVoucherService.list(filters),
    staleTime: 30000,
  });
}

export function useReceiptVoucher(id: string | undefined) {
  return useQuery({
    queryKey: ['receipt-vouchers', id],
    queryFn: () => receiptVoucherService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useArSettlementContext(customerId: string | undefined, currency?: string) {
  return useQuery({
    queryKey: ['ar-settlement-context', customerId, currency],
    queryFn: () => receiptVoucherService.getArSettlementContext(customerId!, currency),
    enabled: !!customerId && !!currency,
    staleTime: 30000,
  });
}

export function useCreateReceiptVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreateReceiptVoucherPayload) => receiptVoucherService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers'] });
      message.success('Receipt voucher created');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create receipt voucher');
    },
  });
}

export function useUpdateReceiptVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateReceiptVoucherPayload> }) =>
      receiptVoucherService.update(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers', id] });
      message.success('Receipt voucher updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update receipt voucher');
    },
  });
}

export function usePostReceiptVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => receiptVoucherService.post(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers', id] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      message.success('Receipt voucher posted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to post receipt voucher');
    },
  });
}

export function useCancelReceiptVoucher() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => receiptVoucherService.cancel(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers', id] });
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] });
      message.success('Receipt voucher cancelled');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to cancel receipt voucher');
    },
  });
}
